
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid } from '@mui/material';
import LeaderBoard from './LeaderBoard';
import AboutPi from './AboutPi';

export default function PiChallenge() {
  const [digits, setDigits] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [correctDigits, setCorrectDigits] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const theme = useTheme();

  const PI = '141592653589793238462643383279502884197169399375105820974944592307816406286';

  const handleStartGame = () => {
    setGameStarted(true);
    setStartTime(Date.now());
    setDigits('');
    setCorrectDigits(0);
  };

  const handleDigitClick = (digit) => {
    if (!gameStarted) return;
    
    const newInput = digits + digit;
    setDigits(newInput);
    
    let correct = 0;
    for (let i = 0; i < newInput.length; i++) {
      if (newInput[i] === PI[i]) {
        correct++;
      } else {
        setGameStarted(false);
        setShowNameDialog(true);
        setCorrectDigits(correct);
        return;
      }
    }
    setCorrectDigits(correct);
  };

  const handleNameSubmit = () => {
    if (playerName.trim()) {
      localStorage.setItem('lastPlayerName', playerName);
      const endTime = Date.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);
      setShowNameDialog(false);
      setDigits('');
      setPlayerName('');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          mb: 3
        }}
      >
        <Typography variant="h4" gutterBottom align="center">
          Pi Challenge
        </Typography>
        
        <Typography variant="body1" gutterBottom align="center">
          Enter the digits of π after 3. (e.g., start with 141592...)
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5">3.</Typography>
            <Typography variant="h5" sx={{ minWidth: '200px', fontFamily: 'monospace' }}>
              {digits || '________'}
            </Typography>
          </Box>

          <Grid container spacing={1} sx={{ maxWidth: '300px' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((digit) => (
              <Grid item xs={4} key={digit}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleDigitClick(digit)}
                  disabled={!gameStarted}
                  sx={{
                    height: '48px',
                    fontSize: '1.25rem'
                  }}
                >
                  {digit}
                </Button>
              </Grid>
            ))}
          </Grid>

          <Button 
            variant="contained" 
            color="primary"
            onClick={handleStartGame}
            disabled={gameStarted}
            sx={{ mt: 2 }}
          >
            {gameStarted ? 'Game in Progress' : 'Start Game'}
          </Button>
        </Box>

        <Typography variant="h6" textAlign="center">
          Correct Digits: {correctDigits}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <AboutPi />
        <LeaderBoard correctDigits={correctDigits} startTime={startTime} />
      </Box>

      <Dialog open={showNameDialog} onClose={() => setShowNameDialog(false)}>
        <DialogTitle>Game Over!</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            You correctly entered {correctDigits} digits of π!
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Enter your name"
            fullWidth
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNameDialog(false)}>Cancel</Button>
          <Button onClick={handleNameSubmit} variant="contained">Submit Score</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
